import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import Sitemap from "../../../routes/sitemap";
import portraitIcon from "../../../assets/images/icons/portrait.svg";
import calculatorMoneyIcon from "../../../assets/images/icons/calculator-money.svg";
import paperPlaneIcon from "../../../assets/images/icons/paper-plane.svg";
import { GlobalQueries } from "hooks/useRefreshQuery";
import { useQuery } from "react-query";
import useAPI from "hooks/useAPI";
import scss from "./LeftSideBar.module.scss";
import CategoriesModal from "../MobileDialogs/CategoriesModal/CategoriesModal";
import icons from "helpers/icons";
import SearchModal from "../MobileDialogs/SearchModal/SearchModal";
import { useTranslation } from "react-i18next";

/**
 * Left side navigation.
 */
const LeftSideBar = ({ openModal, setOpenModal, openCategoriesModal, setOpenCategoriesModal, setOpenCartDialog }) => {
    const staticAppVersion = "1.0.0";

    const { t } = useTranslation();

    const api = useAPI();
    const { pathname } = useLocation();

    // Load all notifications
    const { data: notifications } = useQuery([GlobalQueries.Notifications], () => api.list(`/notifications`));
    const unreadNotificationCount = notifications?.items?.filter((notification) => !notification.seen_date).length;
    const { data: wishlist } = useQuery([GlobalQueries.Wishlist], () => api.list(`/wishlist`));
    const itemsInWishlist = wishlist?.items?.length;

    // Get the class for the icon in the menu
    const iconClass = (icon) => scss.link + (icon.isActive ? " " + scss.active : "");

    const [openSearchModal, setOpenSearchModal] = useState(false);

    const closeAllModals = () => {
        setOpenCategoriesModal(false);
        setOpenSearchModal(false);
    };

    const toggleSearch = () => {
        setOpenModal("sidebar");
        closeAllModals();
        setOpenSearchModal(!openSearchModal);
    };

    const toggleCategories = () => {
        setOpenModal("sidebar");
        setOpenSearchModal(false);
        setOpenCartDialog(false);
        setOpenCategoriesModal((isOpen) => !isOpen);
    };

    useEffect(() => {
        if (openModal !== "sidebar") {
            closeAllModals();
        }
    }, [openModal]);

    useEffect(() => {
        closeAllModals();
    }, [pathname]);

    const links = [
        { icon: icons.catalogue, label: "global-left_side_bar-nav_list-home", to: Sitemap.home, badge: null },
        {
            icon: icons.notifications,
            label: "global-left_side_bar-nav_list-notifications",
            to: Sitemap.notifications._,
            badge: unreadNotificationCount > 0 && <em className={scss.badge}>{unreadNotificationCount}</em>,
        },
        { icon: icons.cart, label: "global-left_side_bar-nav_list-purchase", to: Sitemap.orders._, badge: null },
        { icon: calculatorMoneyIcon, label: "global-left_side_bar-nav_list-analytical", to: Sitemap.static.analyticCard, badge: null },
        { icon: paperPlaneIcon, label: "global-left_side_bar-nav_list-contact", to: Sitemap.contact._, badge: null },
        { icon: icons.wish_list, label: "global-header-wishlist-title", to: Sitemap.wishlist._, badge: itemsInWishlist > 0 && <em className={scss.badge}>{itemsInWishlist}</em> },
    ];

    const renderRailContent = (mobile = false) => (
        <>
            <div className={scss.links}>
                {(mobile ? [...links.slice(1), { icon: icons.suitcase, label: "global-header-sale-title", to: Sitemap.sales._ }] : links).map((link) => (
                    <NavLink to={link.to} key={link.to} className={iconClass} title={t(link.label)} aria-label={t(link.label)} onClick={closeAllModals}>
                        <img src={link.icon} alt="" />
                        {link.badge}
                    </NavLink>
                ))}
            </div>
            <div className={scss.links}>
                <NavLink to={Sitemap.profile._} className={iconClass} title={t("global-left_side_bar-nav_list-profile")} aria-label={t("global-left_side_bar-nav_list-profile")} onClick={closeAllModals}>
                    <img src={portraitIcon} alt="" />
                </NavLink>
                <div className={scss.version}>
                    {process.env.REACT_APP_VERSION || staticAppVersion}
                </div>
            </div>
        </>
    );

    return (
        <>
            <div className={scss.desktopSlot}>
                <div className={`${scss.wrapper} ${scss.navDesktop}`}>
                    {renderRailContent()}
                </div>
            </div>
            <nav className={`${scss.wrapper} ${scss.navMobile}`} aria-label="Mobilni meni">
                <button type="button" onClick={toggleSearch} className={`${scss.link} ${openSearchModal ? scss.active : ""}`} aria-label="Pretraga" aria-expanded={openSearchModal}>
                    <img src={icons.search} alt="" />
                    <span>Pretraga</span>
                </button>
                <button type="button" onClick={toggleCategories} className={`${scss.link} ${openCategoriesModal ? scss.active : ""}`} aria-label="Kategorije" aria-expanded={openCategoriesModal}>
                    <img src={icons.catalogue} alt="" />
                    <span>Kategorije</span>
                </button>
            </nav>

            <CategoriesModal openModal={openCategoriesModal} setOpenModal={setOpenCategoriesModal} sideRail={<nav className={scss.mobileRail} aria-label="Glavna navigacija">{renderRailContent(true)}</nav>} />
            <SearchModal openModal={openSearchModal} setOpenModal={setOpenSearchModal} />
        </>
    );
};

export default LeftSideBar;
